'use client';

import { FC, useMemo, useState } from 'react';
import { Box, Typography, Paper, Stack, Link, MenuItem, Select, Autocomplete, TextField, Chip } from '@mui/material';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useIsDataModified, usePublicationData } from '@/hooks/useTreeDetailSubscriptions';
import { useTree } from '@/hooks/useTree';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useBackend } from '@/hooks/useBackend';
import { getPrivilegeForEntity } from '@/types/tree';
import { PrivilegeEnum, PublicationDto, PublicationTypeDto } from '@/api/ApiClient';
import { log } from '@/lib/utils/logger';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { Footer } from '@/components/details/Footer';
import DetailField from '@/components/common/DetailField';
import { DebouncedTextField } from '@/components/common/DebouncedTextField';
import { useSubmitWithValidation } from '@/hooks/useSubmitWithValidation';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import type { OptionKey } from '@/lib/options/optionTypes';
import { getPublicationTypeDisplay, PUBLICATION_TYPE_DISPLAY } from '@/lib/types/publicationTypes';
import { toUpdatePublicationRequest } from '@/lib/entity/entitySaveUtils';
import { buildPublicationSubmitValidationFields } from '@/lib/publication/publicationSubmitValidation';
import { toDoiHref } from '@/lib/utils/doiLinks';

/**
 * Renders publication details in the detail area and follows the app's standard
 * details pattern: edits update tree state immediately, Save persists to backend.
 */
const PublicationDetails: FC = () => {
  const backend = useBackend();
  const { selectedNode } = useTreeSelection();
  const { updateData, updateDataPartial, setDataModified } = useTree();
  const { showSnackbar } = useSnackbar();
  const { submitWithValidation } = useSubmitWithValidation();
  const selectKeys: OptionKey[] = [
    'publicationType',
    'projects',
    'synthesisInstruments',
    'characterizationInstruments',
  ];
  const { options } = useSelectOptions(selectKeys);

  const [isManuallyEditing, setIsManuallyEditing] = useState(false);

  const publication = usePublicationData(selectedNode) ?? null;
  const isNodeReadOnly = selectedNode?.type === 'publicationInData';
  const dataId = selectedNode?.dataId;
  const isModified = useIsDataModified(dataId);
  const isReadOnly = isNodeReadOnly || (!isModified && !isManuallyEditing);

  const privilege = useMemo(
    () => getPrivilegeForEntity('publication', publication) ?? PrivilegeEnum.Publication,
    [publication]
  );

  const doiLink = publication?.doi ? toDoiHref(publication.doi) : '';

  // Keep DOI path compatibility by encoding each DOI segment individually.
  const dataLink = useMemo(() => {
    if (!publication?.hasData) return '';
    const idOrDoi = publication.doi || String(publication.id);
    const path = idOrDoi
      .split('/')
      .filter((s) => s.length > 0)
      .map((s) => encodeURIComponent(s))
      .join('/');
    return `/data/${path}`;
  }, [publication]);

  const handleSave = async () => {
    if (!selectedNode || !publication) return;
    try {
      const request = toUpdatePublicationRequest(publication);

      const updated = await backend.publication.update(String(publication.id), request);
      updateData(selectedNode.dataId, updated);
      showSnackbar('Publication saved', 'success');
      setDataModified(selectedNode.dataId, false);
      setIsManuallyEditing(false);
    } catch (error) {
      log.error('[PublicationDetails] Failed to save publication:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to save publication.', 'error');
    }
  };

  const handleModification = (modified: Partial<PublicationDto>) => {
    if (!selectedNode) return;
    updateDataPartial(selectedNode.dataId, modified);
  };

  const handleSubmit = async (): Promise<boolean> => {
    if (!selectedNode || !publication) return false;

    const validationFields = buildPublicationSubmitValidationFields({
      title: publication.title,
      authors: publication.authors,
      journal: publication.journal,
      cite: publication.cite,
      doi: publication.doi,
      type: publication.type,
      year: publication.year,
      requireMainScienceDriver: false,
      selectedScienceDriverIds: publication.scienceDriverIds,
    });

    const result = await submitWithValidation(selectedNode, validationFields, 'Publication');
    return result.success;
  };

  if (!publication || (selectedNode?.type !== 'publication' && selectedNode?.type !== 'publicationInData')) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          {!publication ? 'Select a publication to view details' : 'Selected node is not a publication.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 2 }}>
        <Stack spacing={2}>
          <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <DetailField
                  label="Year"
                  value={publication.year || 'N/A'}
                  isReadOnly={isReadOnly}
                  width={{ width: '48%' }}
                >
                  <DebouncedTextField
                    value={publication.year ? String(publication.year) : ''}
                    onUpdate={(value) => handleModification({ year: value ? Number(value) : undefined })}
                    fullWidth
                    size="small"
                    type="number"
                  />
                </DetailField>
                <DetailField
                  label="Journal"
                  value={publication.journal || 'N/A'}
                  isReadOnly={isReadOnly}
                  required
                  width={{ width: '48%' }}
                >
                  <DebouncedTextField
                    value={publication.journal || ''}
                    onUpdate={(value) => handleModification({ journal: value })}
                    fullWidth
                    size="small"
                  />
                </DetailField>
              </Box>

              <DetailField
                label="Title"
                value={<span dangerouslySetInnerHTML={{ __html: publication.title || '' }} />}
                isReadOnly={isReadOnly}
                required
                width={{ width: '100%' }}
              >
                <RichTextEditor
                  value={publication.title || ''}
                  onChange={(value) => handleModification({ title: value })}
                />
              </DetailField>

              <DetailField
                label="Description"
                value={<span dangerouslySetInnerHTML={{ __html: publication.description || '' }} />}
                isReadOnly={isReadOnly}
                width={{ width: '100%' }}
              >
                <RichTextEditor
                  value={publication.description || ''}
                  onChange={(value) => handleModification({ description: value })}
                />
              </DetailField>

              <DetailField
                label="Authors"
                value={<span dangerouslySetInnerHTML={{ __html: publication.authors || '' }} />}
                isReadOnly={isReadOnly}
                required
                width={{ width: '100%' }}
              >
                <RichTextEditor
                  value={publication.authors || ''}
                  onChange={(value) => handleModification({ authors: value })}
                />
              </DetailField>

              <DetailField
                label="Citation"
                value={<span dangerouslySetInnerHTML={{ __html: publication.cite || '' }} />}
                isReadOnly={isReadOnly}
                required
                width={{ width: '100%' }}
              >
                <RichTextEditor
                  value={publication.cite || ''}
                  onChange={(value) => handleModification({ cite: value })}
                />
              </DetailField>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <DetailField
                  label="DOI"
                  value={
                    publication.doi ? (
                      <Link href={doiLink} target="_blank" rel="noopener noreferrer">
                        {publication.doi}
                      </Link>
                    ) : (
                      'N/A'
                    )
                  }
                  isReadOnly={isReadOnly}
                  required
                  width={{ width: '48%' }}
                >
                  <DebouncedTextField
                    value={publication.doi || ''}
                    onUpdate={(value) => handleModification({ doi: value })}
                    fullWidth
                    size="small"
                  />
                </DetailField>
                <DetailField
                  label="Type"
                  value={getPublicationTypeDisplay(publication.type)}
                  isReadOnly={isReadOnly}
                  required
                  width={{ width: '48%' }}
                >
                  <Select
                    value={publication.type || PublicationTypeDto.InHouse}
                    onChange={(e) => handleModification({ type: e.target.value as PublicationTypeDto })}
                    fullWidth
                    size="small"
                    required
                  >
                    {options.publicationType?.length
                      ? options.publicationType.map((typeOption) => (
                          <MenuItem key={typeOption.key} value={typeOption.key}>
                            {typeOption.value}
                          </MenuItem>
                        ))
                      : Object.entries(PUBLICATION_TYPE_DISPLAY).map(([key, label]) => (
                          <MenuItem key={key} value={key}>
                            {label}
                          </MenuItem>
                        ))}
                  </Select>
                </DetailField>
              </Box>

              {dataLink ? (
                <Box>
                  <Typography variant="subtitle2">Data set</Typography>
                  <Link href={dataLink}>Show dataset associated with this publication</Link>
                </Box>
              ) : null}

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <DetailField
                  label="Associated Projects"
                  value={publication.projects?.map((p) => p.projectLabel || p.id).join(', ') || 'None'}
                  isReadOnly={isReadOnly}
                  width={{ width: '48%' }}
                >
                  <Autocomplete
                    multiple
                    options={options.projects || []}
                    getOptionLabel={(option) => option.projectLabel || String(option.id || '')}
                    value={
                      options.projects?.filter((p) => publication.projects?.some((selected) => selected.id === p.id)) ||
                      []
                    }
                    onChange={(_, newValue) =>
                      handleModification({
                        projects: newValue.map((p) => ({ id: p.id, projectLabel: p.projectLabel })),
                      })
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip {...getTagProps({ index })} key={option.id} label={option.projectLabel || option.id} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} fullWidth size="small" placeholder="Select projects" />
                    )}
                  />
                </DetailField>
                <Box sx={{ width: '48%' }} />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <DetailField
                  label="Synthesis Instruments"
                  value={publication.synthesisInstruments?.map((i) => `${i.id} - ${i.name}`).join(', ') || 'None'}
                  isReadOnly={isReadOnly}
                  width={{ width: '48%' }}
                >
                  <Autocomplete
                    multiple
                    options={options.synthesisInstruments || []}
                    getOptionLabel={(option) => `${option.id} - ${option.name}`}
                    value={
                      options.synthesisInstruments?.filter((i) =>
                        publication.synthesisInstruments?.some((selected) => selected.id === i.id)
                      ) || []
                    }
                    onChange={(_, newValue) =>
                      handleModification({
                        synthesisInstruments: newValue.map((i) => ({ id: i.id, name: i.name, doi: i.doi })),
                      })
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip {...getTagProps({ index })} key={option.id} label={`${option.id} - ${option.name}`} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} fullWidth size="small" placeholder="Select synthesis instruments" />
                    )}
                  />
                </DetailField>

                <DetailField
                  label="Characterization Instruments"
                  value={
                    publication.characterizationInstruments?.map((i) => `${i.id} - ${i.name}`).join(', ') || 'None'
                  }
                  isReadOnly={isReadOnly}
                  width={{ width: '48%' }}
                >
                  <Autocomplete
                    multiple
                    options={options.characterizationInstruments || []}
                    getOptionLabel={(option) => `${option.id} - ${option.name}`}
                    value={
                      options.characterizationInstruments?.filter((i) =>
                        publication.characterizationInstruments?.some((selected) => selected.id === i.id)
                      ) || []
                    }
                    onChange={(_, newValue) =>
                      handleModification({
                        characterizationInstruments: newValue.map((i) => ({ id: i.id, name: i.name, doi: i.doi })),
                      })
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip {...getTagProps({ index })} key={option.id} label={`${option.id} - ${option.name}`} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} fullWidth size="small" placeholder="Select characterization instruments" />
                    )}
                  />
                </DetailField>
              </Box>
            </Stack>
          </Paper>
        </Stack>
      </Box>

      {!isNodeReadOnly ? (
        <Box sx={{ flexShrink: 0 }}>
          <Footer
            lastChangedDate={publication.lastChangedDate}
            lastChangedBy={publication.lastChangedByUser}
            status={publication.status}
            isReadOnly={isReadOnly}
            setIsReadOnly={(val) => setIsManuallyEditing(!val)}
            onSave={handleSave}
            privilege={privilege}
            isFormValid={true}
            onSubmit={handleSubmit}
            hideWorkflowActions
            ignoreStatusForDelete
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default PublicationDetails;
